import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import SettingsSection from './SettingsSection';
import SettingItem from './SettingItem';
import ToggleSwitch from './ToggleSwitch';
import useNotification from '../../../hooks/useNotification';

/**
 * Enhanced notifications tab component for VA profile settings
 * Now uses NotificationContext with Redis caching for optimal performance
 */
const VANotificationsTab = ({ profileData, onInputChange }) => {
  const { preferences, updatePreferences, isLoading: preferencesLoading } = useNotification();
  const [localSettings, setLocalSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Initialize local settings from context preferences or profileData
  useEffect(() => {
    if (preferences) {
      // Use preferences from NotificationContext (Redis-cached)
      setLocalSettings(preferences);
    } else if (profileData?.notification_settings) {
      // Fallback to profileData
      setLocalSettings(profileData.notification_settings);
    } else {
      // Default settings
      setLocalSettings({
        email: {
          jobMatches: true,
          applicationUpdates: true,
          messages: true,
          paymentUpdates: true,
          profileViews: false,
          weeklyDigest: true,
          marketingEmails: false,
          securityAlerts: true,
        },
        push: {
          messages: true,
          applicationUpdates: true,
          jobMatches: false
        },
        frequency: {
          digestFrequency: 'daily'
        }
      });
    }
  }, [preferences, profileData]);
  
  // Get notification settings with fallback
  const notificationSettings = localSettings || {
    email: {},
    push: {},
    frequency: { digestFrequency: 'daily' }
  };
  
  // Handle notification setting changes
  const handleToggleChange = async (setting, value) => {
    if (!localSettings) return;
    
    let newSettings;
    
    // Handle nested settings structure
    if (setting.includes('.')) {
      const [category, key] = setting.split('.');
      newSettings = {
        ...localSettings,
        [category]: {
          ...localSettings[category],
          [key]: value
        }
      };
    } else {
      // Handle frequency settings that are at the root level
      if (setting === 'digestFrequency') {
        newSettings = {
          ...localSettings,
          frequency: {
            ...localSettings.frequency,
            digestFrequency: value
          }
        };
      } else {
        // Handle email settings (legacy support)
        newSettings = {
          ...localSettings,
          email: {
            ...localSettings.email,
            [setting]: value
          }
        };
      }
    }
    
    // Update local state optimistically for instant UI feedback
    setLocalSettings(newSettings);
    
    // Log for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('VANotificationsTab: Updating notification preferences:', {
        setting,
        value,
        newSettings
      });
    }
    
    // Save to NotificationContext (Redis-cached)
    setIsSaving(true);
    try {
      const result = await updatePreferences(newSettings);
      
      if (result.success) {
        // Also update parent component's profile data for consistency
        onInputChange('notification_settings', newSettings);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Notification preferences saved (Redis cache invalidated)');
        }
      } else {
        console.error('❌ Failed to save preferences:', result.error);
        // Revert optimistic update on failure
        setLocalSettings(preferences || profileData?.notification_settings);
      }
    } catch (error) {
      console.error('❌ Exception saving preferences:', error);
      // Revert optimistic update on error
      setLocalSettings(preferences || profileData?.notification_settings);
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state
  if (preferencesLoading && !localSettings) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Get email settings for component rendering
  const emailSettings = notificationSettings.email || {};

  return (
    <div className="space-y-6">
      {/* Saving indicator */}
      {isSaving && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-900">Saving preferences...</span>
        </div>
      )}
      
      {/* Email Notifications */}
      <SettingsSection
        title="Email Notifications"
        description="Manage when and how you receive email notifications"
        icon="Mail"
      >
        <div className="space-y-4">
          <SettingItem
            title="Job Matches"
            description="Get notified when jobs matching your skills are posted"
            control={
              <ToggleSwitch
                checked={emailSettings.jobMatches ?? false}
                onChange={(checked) => handleToggleChange('jobMatches', checked)}
              />
            }
          />

          <SettingItem
            title="Application Updates"
            description="Updates on your job applications (shortlisted, rejected, etc.)"
            control={
              <ToggleSwitch
                checked={emailSettings.applicationUpdates ?? false}
                onChange={(checked) => handleToggleChange('applicationUpdates', checked)}
              />
            }
          />

          <SettingItem
            title="Messages"
            description="New messages from employers and support"
            control={
              <ToggleSwitch
                checked={emailSettings.messages ?? false}
                onChange={(checked) => handleToggleChange('messages', checked)}
              />
            }
          />

          <SettingItem
            title="Payment Updates"
            description="Payment confirmations and invoice updates"
            control={
              <ToggleSwitch
                checked={emailSettings.paymentUpdates ?? false}
                onChange={(checked) => handleToggleChange('paymentUpdates', checked)}
              />
            }
          />

          <SettingItem
            title="Profile Views"
            description="When employers view your profile"
            control={
              <ToggleSwitch
                checked={emailSettings.profileViews ?? false}
                onChange={(checked) => handleToggleChange('profileViews', checked)}
              />
            }
          />

          <SettingItem
            title="Weekly Digest"
            description="Weekly summary of new opportunities and platform updates"
            control={
              <ToggleSwitch
                checked={emailSettings.weeklyDigest ?? false}
                onChange={(checked) => handleToggleChange('weeklyDigest', checked)}
              />
            }
          />

          <SettingItem
            title="Security Alerts"
            description="Important security notifications about your account"
            control={
              <ToggleSwitch
                checked={emailSettings.securityAlerts ?? false}
                onChange={(checked) => handleToggleChange('securityAlerts', checked)}
              />
            }
          />

          <SettingItem
            title="Marketing Emails"
            description="Platform updates, tips, and promotional content"
            control={
              <ToggleSwitch
                checked={emailSettings.marketingEmails ?? false}
                onChange={(checked) => handleToggleChange('marketingEmails', checked)}
              />
            }
          />

          
        </div>
      </SettingsSection>

      {/* Push Notifications */}
      <SettingsSection
        title="Push Notifications"
        description="Real-time notifications in your browser"
        icon="Bell"
      >
        <div className="space-y-4">
          <SettingItem
            title="Messages"
            description="Instant notifications for new messages"
            control={
              <ToggleSwitch
                checked={notificationSettings.push?.messages ?? false}
                onChange={(checked) => handleToggleChange('push.messages', checked)}
              />
            }
          />

          <SettingItem
            title="Application Updates"
            description="Real-time updates on your applications"
            control={
              <ToggleSwitch
                checked={notificationSettings.push?.applicationUpdates ?? false}
                onChange={(checked) => handleToggleChange('push.applicationUpdates', checked)}
              />
            }
          />

          <SettingItem
            title="Job Matches"
            description="Instant alerts for new job matches"
            control={
              <ToggleSwitch
                checked={notificationSettings.push?.jobMatches ?? false}
                onChange={(checked) => handleToggleChange('push.jobMatches', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Email Frequency */}
      <SettingsSection
        title="Email Frequency"
        description="Control how often you receive digest emails"
        icon="Clock"
      >
        <div className="space-y-4">
          <SettingItem
            title="Digest Frequency"
            description="How often to receive summary emails"
            control={
              <select
                value={notificationSettings.frequency?.digestFrequency || 'daily'}
                onChange={(e) => handleToggleChange('digestFrequency', e.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-surface focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="immediate">Immediate</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="never">Never</option>
              </select>
            }
          />
        </div>
      </SettingsSection>

      {/* Information Section */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-medium text-text-primary">Email Delivery & Privacy</h4>
            <div className="text-sm text-text-secondary space-y-2">
              <p>
                All email notifications are sent from our secure servers and include unsubscribe options.
                We never share your email with third parties.
              </p>
              <ul className="list-disc list-inside space-y-1 pl-4">
                <li>Security alerts cannot be disabled for account safety</li>
                <li>Critical system notifications are always sent regardless of settings</li>
                <li>You can update these preferences anytime</li>
                <li>Changes take effect immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VANotificationsTab;